import { ComponentType } from '@angular/cdk/overlay';
import {
  Component,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/select';
import { WidgetPlanningAccuracyChangeDetectionObject } from '@api-response-types/forecast-series.response';
import { GetPlanningAccuracyWidgetDataDto } from '@api-types/dtos/get-planning-accuracy-widget-data.dto';
import { Resolution, toResolutionUnit } from '@api-types/enums/resolution.enum';
import { unixTimeToDesiredResolutionUnixTime } from '@api-types/helper/date-resolution';
import { FilterValue } from '@api-types/types/filter-value.type';
import { H3Component, PmButtonComponent, PmTooltipDirective } from '@libs/ui';
import { LocaleService } from '@core/services/locale.service';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { ChartColorService } from 'demand-forecasting/services/chart/chart-color.service';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { selectAvailableFiltersAsRecord } from 'demand-forecasting/store/filter/filter.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import Highcharts, { numberFormat } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { LucideScan } from '@lucide/angular';
import { finalize } from 'rxjs';
import { DefaultSelectionPlanningAccuracyWidget } from '../../types/types';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';

interface PlanningAccuracyWidgetPointOptions
  extends Highcharts.PointOptionsObject {
  relativeError: number;
  createdAtOfForecastVersion: string;
}
@Component({
  selector: 'pm-planning-accuracy-widget',
  templateUrl: './planning-accuracy-widget.component.html',
  imports: [
    MatCardModule,
    MatLabel,
    MatFormField,
    TranslateModule,
    WidgetBaseComponent,
    WidgetTitleDirective,
    WidgetHeaderDirective,
    WidgetActionsDirective,
    WidgetContentDirective,
    PmTooltipDirective,
    HighchartsChartComponent,
    FormsModule,
    MatInput,
    H3Component,
    PmButtonComponent,
  ],
})
export class PlanningAccuracyWidgetComponent
  extends WidgetBaseComponent<PlanningAccuracyWidgetComponent>
  implements OnInit
{
  @Input({
    required: false,
  })
  resolution?: Resolution;

  @Input({
    required: false,
  })
  planningPeriod?: number;

  @Input({
    required: false,
  })
  selectedFilterGroup?: string;

  @Input({
    required: false,
  })
  selectedFilterOption?: FilterValue;

  @Input({
    required: false,
  })
  additionalTimeSeriesName?: string;

  private readonly chartColorService = inject(ChartColorService);
  private readonly store = inject(Store);
  private readonly localeService = inject(LocaleService);

  protected readonly scanIcon = LucideScan;

  configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  availableFilters = this.store.selectSignal(selectAvailableFiltersAsRecord);

  selectedForecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  protected chartOptions = signal<HighchartsOptions>(
    BaseChartOptions.getEmptyChartOptions(),
  );

  isError = false;
  isNoDataAvailable = false;

  previousPlanningAccuracyChangeDetectionObject: WidgetPlanningAccuracyChangeDetectionObject;

  private readonly insightsHttpClient = inject(InsightsHttpClient);
  private readonly translateService = inject(TranslateService);
  public readonly data: DefaultSelectionPlanningAccuracyWidget = inject(
    MAT_DIALOG_DATA,
    { optional: true },
  );

  constructor() {
    super();
    effect(() => {
      const forecastVersion = this.selectedForecastVersion();
      const availableFilters = this.availableFilters();
      // wait until the available filters are loaded before initializing the widget
      const areFiltersAvailable =
        availableFilters && availableFilters.length > 0;
      if (!forecastVersion || !this.resolution || !areFiltersAvailable) {
        return;
      }

      const newSeriesChangeDetectionObject: WidgetPlanningAccuracyChangeDetectionObject =
        {
          // _resultSetStatus: forecastVersion.status, // TODO do we need the forecast version status here?
          configurationId: forecastVersion.configurationId,
          resultSetId: forecastVersion.id,
          resolution: this.resolution,
        };

      if (
        JSON.stringify(newSeriesChangeDetectionObject) ===
        JSON.stringify(this.previousPlanningAccuracyChangeDetectionObject ?? {})
      ) {
        console.warn('[WIDGET_PLANNING_ACCURACY] No changes');
        return;
      }

      this.previousPlanningAccuracyChangeDetectionObject =
        newSeriesChangeDetectionObject;

      this.selectedFilterGroup =
        this.selectedFilterGroup ?? this.defaultFilterGroup;
      this.selectedFilterOption =
        this.selectedFilterOption ?? this.defaultFilterOption;

      this.getPlanningAccuracyData(false);
    });
  }

  ngOnInit(): void {
    if (this.data) {
      this.resolution = this.data.resolution;
      this.planningPeriod = this.data.planningPeriod;
      this.selectedFilterGroup = this.data.filterGroup;
      this.selectedFilterOption = this.data.filterOption;
    }
  }

  getPlanningAccuracyData(isSelectedFilterGroupChanged: boolean): void {
    this.isLoading = true;
    this.isError = false;

    const configurationId = this.configuration()?.id;
    const forecastVersionId = this.selectedForecastVersion()?.id;
    const availableFilters = this.availableFilters();

    if (isSelectedFilterGroupChanged) {
      this.selectedFilterOption = this.defaultFilterOption;
    }
    if (configurationId && forecastVersionId && availableFilters) {
      this.insightsHttpClient
        .getPlanningAccuracyData(
          configurationId,
          forecastVersionId,
          this.planningPeriod ?? this.defaultPlanningPeriod,
          this.selectedFilterGroup ?? this.defaultFilterGroup,
          this.selectedFilterOption ?? this.defaultFilterOption,
          this.resolution,
          this.additionalTimeSeriesName,
        )
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (data) => {
            this.setChartOptions(data);
            if (
              data.actual.length === 0 &&
              data.forecast.length === 0 &&
              data.additionalTimeSeries.length === 0
            ) {
              this.isNoDataAvailable = true;
            }
          },
          error: () => {
            this.isError = true;
          },
        });
    }
  }

  setChartOptions(
    getPlanningAccuracyWidgetDataDto: GetPlanningAccuracyWidgetDataDto,
  ) {
    const translatedDeviation = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.PLANNING_ACCURACY.DEVIATION',
    );
    const translatedFrom = this.translateService.instant('UTILITY.FROM');
    const locale = this.localeService.getCurrentUserLocale();

    const chartOptions = new ChartOptionsBuilder()
      .withXAxis({
        type: 'datetime',
        title: {
          text:
            this.configuration()?.startForecastConfig?.date_column || 'date',
        },
      })
      .withYAxis({
        title: {
          text: this.configuration()?.startForecastConfig?.target || 'value',
        },
        min: 0,
      })
      .withSeries({
        id: 'actual',
        type: 'spline',
        data: this.limitTimeRangeAndAdjustDates(
          getPlanningAccuracyWidgetDataDto.actual,
        ),
        name: 'Actual',
        marker: { enabled: !this.isDialogInstance },
        showInLegend: true,
        color: BaseChartOptions.getEmptyChartOptions()?.colors?.at(0),
      })
      .withSeries({
        id: 'forecast',
        type: 'spline',
        dashStyle: 'ShortDot',
        data: this.limitTimeRangeAndAdjustDates(
          getPlanningAccuracyWidgetDataDto.forecast,
        ),
        name: 'Forecast',
        showInLegend: true,
        marker: { enabled: !this.isDialogInstance },
        color: BaseChartOptions.getEmptyChartOptions()?.colors?.at(1),
      })
      .withTooltip({
        useHTML: true,
        headerFormat:
          '<table class="font-body"><tr><th colspan="2">{point.key}</th></tr>',
        pointFormatter() {
          const customPointOptions = this
            .options as PlanningAccuracyWidgetPointOptions;
          let tooltip = `<tr>
            <td style="color: ${this.series.color}">
              ${this.series.name} ${this.series.name === 'Forecast' ? `(${translatedFrom} ${new Date(customPointOptions.createdAtOfForecastVersion).toLocaleDateString(locale)})` : ''}
            </td>
            <td style="text-align: right">
              <b>
                ${numberFormat(this.y ?? 0, 2)}
              </b>
            </td>
          </tr>`;

          if (this.series.name === 'Forecast') {
            tooltip += `<tr>
              <td>
                 ${translatedDeviation}
                </td>
              <td style="text-align: right">
                <b>
                ${customPointOptions.relativeError !== null ? `${customPointOptions.relativeError * 100 >= 0 ? '+' : ''}${(customPointOptions.relativeError * 100).toFixed(2)}%` : '\u221E'}
                </b>
              </td>
            </tr>`;
          }
          return tooltip;
        },
        footerFormat: '</table>',
        shared: true,
      })
      .build();

    if (this.additionalTimeSeriesName) {
      chartOptions.series.push({
        id: 'additionalTimeSeries',
        type: 'spline',
        data: this.limitTimeRangeAndAdjustDates(
          getPlanningAccuracyWidgetDataDto.additionalTimeSeries,
        ),
        name: this.additionalTimeSeriesName,
        marker: { enabled: !this.isDialogInstance },
        showInLegend: true,
        color: this.chartColorService.getAdditionalTimeSeriesColors(1)[0],
      });
    }

    this.chartOptions.set(chartOptions);
  }

  limitTimeRangeAndAdjustDates(
    chartSeriesData: [number, number][] | PlanningAccuracyWidgetPointOptions[],
  ) {
    let typedChartSeriesData:
      | [number, number][]
      | PlanningAccuracyWidgetPointOptions[];
    if (this.isDialogInstance) {
      return chartSeriesData;
    }

    const configuration = this.configuration();

    if (chartSeriesData[0] instanceof Array && configuration) {
      typedChartSeriesData = chartSeriesData as [number, number][];
      typedChartSeriesData = typedChartSeriesData.map((point) => {
        const modifiedPoint = point;
        modifiedPoint[0] = unixTimeToDesiredResolutionUnixTime(
          point[0],
          this.resolution || configuration.startForecastConfig.resolution,
        );
        return modifiedPoint;
      });
    } else {
      typedChartSeriesData =
        chartSeriesData as PlanningAccuracyWidgetPointOptions[];
      typedChartSeriesData = typedChartSeriesData.map((point) => {
        const modifiedPoint = point;
        if (modifiedPoint.x && configuration) {
          modifiedPoint.x = unixTimeToDesiredResolutionUnixTime(
            Number(point.x),
            this.resolution || configuration.startForecastConfig.resolution,
          );
        }
        return modifiedPoint;
      });
    }

    const maxNumberOfDataPoints = 25;
    return typedChartSeriesData.length > maxNumberOfDataPoints
      ? typedChartSeriesData.slice(-maxNumberOfDataPoints)
      : typedChartSeriesData;
  }

  // Override the showFullScreen method from the WidgetBaseComponent to handle afterClosed event properly
  showFullScreen(
    componentType: ComponentType<PlanningAccuracyWidgetComponent>,
    filterSelection?: DefaultSelectionPlanningAccuracyWidget,
  ): MatDialogRef<PlanningAccuracyWidgetComponent> | null {
    const dialog = super.showFullScreen(componentType, filterSelection);
    dialog?.afterClosed().subscribe(() => {
      this.selectedFilterGroup = dialog.componentInstance.selectedFilterGroup;
      this.selectedFilterOption = dialog.componentInstance.selectedFilterOption;
      this.getPlanningAccuracyData(false);
    });
    return null;
  }

  get defaultFilterGroup(): string {
    return this.availableFilters()?.[0]?.column;
  }

  get defaultFilterOption(): FilterValue {
    const availableFilters = this.availableFilters();
    if (!availableFilters || availableFilters.length === 0) {
      return '';
    }
    return (
      availableFilters?.find(
        (filter) => filter.column === this.selectedFilterGroup,
      )?.values[0] ?? availableFilters?.[0].values[0]
    );
  }

  get defaultPlanningPeriod() {
    return 1;
  }

  get infoTooltipText(): string {
    const configuration = this.configuration();
    if (configuration) {
      return this.translateService.instant(
        'RESULT.INSIGHTS.WIDGETS.PLANNING_ACCURACY.INFO_TEXT',
        {
          planningPeriod: this.planningPeriod || this.defaultPlanningPeriod,
          resolution: this.translateService.instant(
            toResolutionUnit(
              this.resolution ?? configuration.startForecastConfig.resolution,
              this.planningPeriod || this.defaultPlanningPeriod,
            ),
          ),
        },
      );
    }
    return '';
  }

  get PlanningAccuracyWidgetComponent(): typeof PlanningAccuracyWidgetComponent {
    return PlanningAccuracyWidgetComponent;
  }
}
